import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Trash2, Play, Loader2, ChevronDown, ChevronRight, ChevronUp, Copy, Check, Eye, EyeOff, RefreshCw, AlertTriangle, FileText, GitBranch, Database, Plus, Link2, Zap, FileCode, Wifi, WifiOff, Settings2 } from 'lucide-react';
import { NODE_TYPES } from './nodeTypes';
import ReferenceField from './ReferenceField';
import RuleBuilder from './RuleBuilder';
import { generateMockOutput, inferSchema } from './mockNodeExecution';
import { apiRequest } from '../../config/api';
import ResourceSelector from './ResourceSelector';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';

function ConfigField({ label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, ...props }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-300 transition-colors"
      {...props}
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors bg-white"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function RichSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const SelectedIcon = selected?.icon;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-[13px] border border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors text-left"
      >
        {SelectedIcon && (
          <span className="flex-shrink-0 w-7 h-7 rounded-md bg-gray-50 text-gray-500 flex items-center justify-center">
            <SelectedIcon className="w-3.5 h-3.5" />
          </span>
        )}
        <span className="flex-1 min-w-0 text-gray-900 truncate">{selected?.label}</span>
        {selected?.recommended && (
          <span
            className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500"
            title="Most common"
            aria-label="Most common"
          />
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {options.map((opt) => {
            const Icon = opt.icon;
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full flex items-start gap-2.5 px-2.5 py-2 text-left hover:bg-gray-50 transition-colors ${isSelected ? 'bg-gray-50' : ''}`}
              >
                {Icon && (
                  <span className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-white border border-gray-100 text-gray-500 flex items-center justify-center">
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[13px] font-medium text-gray-900">{opt.label}</span>
                    {opt.recommended && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded whitespace-nowrap">
                        <span className="w-1 h-1 rounded-full bg-emerald-500" />
                        Most common
                      </span>
                    )}
                  </span>
                  {opt.description && (
                    <span className="block text-[11px] text-gray-500 leading-snug mt-0.5">{opt.description}</span>
                  )}
                </span>
                {isSelected && <Check className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-1" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdvancedSettings({ children, label = 'Advanced settings' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1 mb-3 border-t border-gray-100 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
      >
        <Settings2 className="w-3 h-3" />
        <span>{label}</span>
        {open ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function NumberInput({ value, onChange, min, max, placeholder }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
      min={min}
      max={max}
      placeholder={placeholder}
      className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-300 transition-colors"
    />
  );
}

function ConnectionPicker({ provider, value, onChange }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadConnections = useCallback(() => {
    setLoading(true);
    apiRequest(`/connections?provider=${provider}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { setConnections(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [provider]);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const { connect } = useOAuthPopup({
    onSuccess: () => { loadConnections(); },
    onError: (err) => { console.error('OAuth error:', err); },
  });

  if (loading) {
    return (
      <ConfigField label="Google Account">
        <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading accounts...</div>
      </ConfigField>
    );
  }

  if (connections.length === 0) {
    return (
      <ConfigField label="Google Account">
        <button
          onClick={() => connect('google')}
          className="w-full px-2.5 py-2 text-[13px] font-medium border border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
        >
          <Link2 className="w-3.5 h-3.5" />
          Connect Google Account
        </button>
      </ConfigField>
    );
  }

  const activeConnections = connections.filter((c) => c.is_active && c.status === 'connected');

  return (
    <ConfigField label="Google Account" hint="Select which Google account to use">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors bg-white"
      >
        <option value="">Select an account...</option>
        {activeConnections.map((conn) => (
          <option key={conn.id} value={conn.id}>
            {conn.account_identifier}
            {conn.metadata_json?.hd ? ` (${conn.metadata_json.hd})` : ' (Personal)'}
          </option>
        ))}
      </select>
      <button
        onClick={() => connect('google')}
        className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 transition-colors"
      >
        <Plus className="w-3 h-3" />
        Connect another account
      </button>
    </ConfigField>
  );
}

function WebhookConfig({ workflowId }) {
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null); // 'url' | 'secret'
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (!workflowId) return;
    setLoading(true);
    apiRequest(`/workflows/${workflowId}/webhook`, { method: 'POST' })
      .then((r) => r.json())
      .then((data) => { setWebhook(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [workflowId]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const regenerateSecret = async () => {
    const resp = await apiRequest(`/workflows/${workflowId}/webhook`, {
      method: 'PATCH',
      body: JSON.stringify({ regenerate_secret: true }),
    });
    if (resp.ok) {
      const data = await resp.json();
      setWebhook(data);
      setShowSecret(true);
    }
  };

  const toggleActive = async () => {
    const resp = await apiRequest(`/workflows/${workflowId}/webhook`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: !webhook.is_active }),
    });
    if (resp.ok) {
      const data = await resp.json();
      setWebhook(data);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading webhook...</div>;
  if (!webhook) return <p className="text-sm text-red-500">Failed to load webhook. Save the workflow first.</p>;

  return (
    <div className="space-y-3">
      <ConfigField label="Webhook URL">
        <div className="flex gap-1">
          <input
            type="text"
            readOnly
            value={webhook.webhook_url}
            className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50 text-gray-600 truncate"
          />
          <button
            onClick={() => copyToClipboard(webhook.webhook_url, 'url')}
            className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            {copied === 'url' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
          </button>
        </div>
      </ConfigField>

      <ConfigField label="Secret" hint="Send as X-Webhook-Secret header">
        <div className="flex gap-1">
          <input
            type={showSecret ? 'text' : 'password'}
            readOnly
            value={webhook.secret || ''}
            className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-gray-50 text-gray-600"
          />
          <button onClick={() => setShowSecret((v) => !v)} className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0">
            {showSecret ? <EyeOff className="w-3.5 h-3.5 text-gray-400" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
          </button>
          <button
            onClick={() => copyToClipboard(webhook.secret, 'secret')}
            className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            {copied === 'secret' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
          </button>
        </div>
        <button onClick={regenerateSecret} className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700">
          <RefreshCw className="w-3 h-3" /> Regenerate secret
        </button>
      </ConfigField>

      <ConfigField label="Active">
        <button
          onClick={toggleActive}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${webhook.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
        >
          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${webhook.is_active ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
        </button>
      </ConfigField>
    </div>
  );
}

function FormSubmissionConfig({ config, onChange, workflowId, onOutputSchemaUpdate, onSaveAndNavigate }) {
  const update = (key, value) => onChange({ ...config, [key]: value });
  const [fieldsPreview, setFieldsPreview] = useState(null);
  const [loadingFields, setLoadingFields] = useState(false);
  const [versionMismatch, setVersionMismatch] = useState(false);

  // Load fields when form is selected
  useEffect(() => {
    if (!config.formId) { setFieldsPreview(null); return; }
    setLoadingFields(true);
    apiRequest(`/forms/${config.formId}/fields-preview`)
      .then((r) => r.json())
      .then((data) => {
        setFieldsPreview(data);
        setLoadingFields(false);

        if (config.formVersion && data.current_version !== config.formVersion) {
          setVersionMismatch(true);
        } else {
          setVersionMismatch(false);
        }

        if (!config.formVersion) {
          updateOutputSchema(data);
        }
      })
      .catch(() => setLoadingFields(false));
  }, [config.formId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateOutputSchema = (data) => {
    const schema = data.fields.map((f) => ({
      path: `data.${f.id}`,
      label: f.label || f.slug || f.id,
      type: f.type === 'number' ? 'number' : 'string',
    }));
    schema.unshift(
      { path: 'form_id', label: 'Form ID', type: 'string' },
      { path: 'response_id', label: 'Response ID', type: 'string' },
      { path: 'submitted_at', label: 'Submitted At', type: 'string' },
    );
    update('formVersion', data.current_version);
    if (onOutputSchemaUpdate) onOutputSchemaUpdate(schema);
    setVersionMismatch(false);
  };

  const handleRefreshFields = () => {
    if (fieldsPreview) {
      updateOutputSchema(fieldsPreview);
    }
  };

  return (
    <div className="space-y-3">
      <ResourceSelector
        label="Form"
        value={config.formId}
        onChange={(v) => update('formId', v)}
        endpoint="/forms/"
        mapItem={(f) => ({ value: f.id, label: f.title })}
        placeholder="Select a form…"
        emptyIcon={FileText}
        emptyTitle="No forms yet"
        emptyMessage="Create a form first, then come back to connect it as a trigger."
        emptyAction="Save Work & Create Form"
        onEmptyAction={() => onSaveAndNavigate('/forms')}
      />

      {versionMismatch && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[11px] text-amber-700 font-medium">Form has been updated</p>
            <p className="text-[11px] text-amber-600">Fields may have changed since this trigger was configured.</p>
            <button onClick={handleRefreshFields} className="mt-1 flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 font-medium">
              <RefreshCw className="w-3 h-3" /> Refresh Fields
            </button>
          </div>
        </div>
      )}

      {loadingFields && (
        <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading fields…</div>
      )}

      {fieldsPreview && !loadingFields && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Form Fields</label>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-2 py-1 font-medium text-gray-500">Field</th>
                  <th className="text-left px-2 py-1 font-medium text-gray-500">Type</th>
                  <th className="text-left px-2 py-1 font-medium text-gray-500">Req</th>
                </tr>
              </thead>
              <tbody>
                {fieldsPreview.fields.map((field) => (
                  <tr key={field.id} className="border-b border-gray-100 last:border-0">
                    <td className="px-2 py-1 text-gray-700">{field.label || field.slug}</td>
                    <td className="px-2 py-1 text-gray-500">{field.type}</td>
                    <td className="px-2 py-1">
                      {field.required ? (
                        <span className="text-[10px] bg-red-50 text-red-600 px-1 py-0.5 rounded">req</span>
                      ) : (
                        <span className="text-[10px] bg-gray-50 text-gray-400 px-1 py-0.5 rounded">opt</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CrmRecordConfig({ nodeType, config, onChange, upstreamNodes, onOutputSchemaUpdate }) {
  const update = (key, value) => onChange({ ...config, [key]: value });
  const updateFieldValue = (slug, value) => {
    const fieldValues = { ...(config.fieldValues || {}) };
    fieldValues[slug] = value;
    onChange({ ...config, fieldValues });
  };

  const [objectDefs, setObjectDefs] = useState([]);
  const [loadingDefs, setLoadingDefs] = useState(true);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedObjectName, setSelectedObjectName] = useState('');

  // Load object definitions on mount
  useEffect(() => {
    setLoadingDefs(true);
    apiRequest('/crm/objects/')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { setObjectDefs(data); setLoadingDefs(false); })
      .catch(() => setLoadingDefs(false));
  }, []);

  // Load field definitions when object type changes
  const loadFields = useCallback((objDefId) => {
    if (!objDefId) { setFieldDefs([]); return; }
    setLoadingFields(true);
    apiRequest(`/crm/objects/${objDefId}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        const fields = data.field_definitions || [];
        setFieldDefs(fields);
        setSelectedObjectName(data.name || '');
        setLoadingFields(false);

        // Update output schema dynamically
        if (onOutputSchemaUpdate) {
          const showsRecord = ['createRecord', 'getRecord', 'updateRecord'].includes(nodeType);
          if (showsRecord) {
            const schema = [
              { path: 'record', label: `${data.name || 'Record'}`, type: 'object' },
              { path: 'record.id', label: 'Record ID', type: 'string' },
              { path: 'record.object_definition_id', label: 'Object Definition ID', type: 'string' },
              ...fields.map((f) => ({
                path: `record.data.${f.slug}`,
                label: f.name,
                type: f.field_type === 'number' ? 'number' : f.field_type === 'boolean' ? 'boolean' : 'string',
              })),
              { path: 'record.created_at', label: 'Created At', type: 'string' },
              { path: 'record.updated_at', label: 'Updated At', type: 'string' },
            ];
            onOutputSchemaUpdate(schema);
          } else if (nodeType === 'searchRecords') {
            const schema = [
              { path: 'results', label: 'Matches', type: 'array' },
              ...fields.map((f) => ({
                path: `results[].data.${f.slug}`,
                label: f.name,
                type: f.field_type === 'number' ? 'number' : f.field_type === 'boolean' ? 'boolean' : 'string',
              })),
              { path: 'results[].id', label: 'Record ID', type: 'string' },
              { path: 'count', label: 'Number of Matches', type: 'number' },
              { path: 'total', label: 'Total Available', type: 'number' },
            ];
            onOutputSchemaUpdate(schema);
          }
        }
      })
      .catch(() => setLoadingFields(false));
  }, [nodeType, onOutputSchemaUpdate]);

  useEffect(() => {
    loadFields(config.objectDefinitionId);
  }, [config.objectDefinitionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const showFieldInputs = ['createRecord', 'updateRecord'].includes(nodeType);
  const showRecordId = ['getRecord', 'updateRecord', 'deleteRecord'].includes(nodeType);
  const showSearch = nodeType === 'searchRecords';

  return (
    <div className="space-y-3">
      {/* Object Type Selector */}
      <ConfigField label="Object Type">
        {loadingDefs ? (
          <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : (
          <SelectInput
            value={config.objectDefinitionId || ''}
            onChange={(v) => {
              const updated = { ...config, objectDefinitionId: v, fieldValues: {} };
              if ('recordId' in config) updated.recordId = '';
              onChange(updated);
            }}
            options={[
              { value: '', label: 'Select an object type…' },
              ...objectDefs.map((od) => ({ value: od.id, label: od.name })),
            ]}
          />
        )}
      </ConfigField>

      {/* Record ID field */}
      {showRecordId && (
        <ReferenceField
          label="Record ID"
          hint="Use output from a previous step"
          value={config.recordId || ''}
          onChange={(v) => update('recordId', v)}
          placeholder="{{previous_step.record.id}}"
          upstreamNodes={upstreamNodes}
        />
      )}

      {/* Search fields */}
      {showSearch && (
        <>
          {/* Legacy free-text query warning — show until user converts to filter rules */}
          {config.query && !config.filter && (
            <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[11px] text-amber-700 font-medium">Reconfigure required</p>
                <p className="text-[11px] text-amber-600">This node uses the legacy free-text search. Set up filter rules below and remove the legacy query.</p>
                <button
                  type="button"
                  onClick={() => onChange({ ...config, query: '' })}
                  className="mt-1 text-[11px] text-amber-700 hover:text-amber-900 font-medium underline"
                >
                  Remove legacy query
                </button>
              </div>
            </div>
          )}

          <ConfigField label="Filter Rules" hint="Match records by field rules. Values support {{node.path}} references.">
            <RuleBuilder
              filter={config.filter}
              onChange={(filter) => update('filter', filter)}
              fieldDefs={fieldDefs}
              upstreamNodes={upstreamNodes}
            />
          </ConfigField>
          <ConfigField label="Limit">
            <NumberInput value={config.limit} onChange={(v) => update('limit', v)} min={1} max={100} placeholder="10" />
          </ConfigField>
        </>
      )}

      {/* Dynamic field inputs */}
      {showFieldInputs && config.objectDefinitionId && (
        <>
          {loadingFields ? (
            <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading fields…</div>
          ) : fieldDefs.length > 0 ? (
            <>
              <div className="flex items-center gap-1.5 mt-1">
                <Database className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {selectedObjectName} Fields
                </span>
              </div>
              {fieldDefs.map((field) => {
                if (field.field_type === 'boolean') {
                  return (
                    <ConfigField key={field.id} label={field.name + (field.is_required ? ' *' : '')}>
                      <SelectInput
                        value={
                          ((config.fieldValues || {})[field.slug] === true || (config.fieldValues || {})[field.slug] === false)
                            ? String((config.fieldValues || {})[field.slug])
                            : (config.fieldValues || {})[field.slug] ?? ''
                        }
                        onChange={(v) => updateFieldValue(field.slug, v === 'true' ? true : v === 'false' ? false : v)}
                        options={[
                          { value: '', label: '— Leave empty —' },
                          { value: 'true', label: 'True' },
                          { value: 'false', label: 'False' },
                        ]}
                      />
                    </ConfigField>
                  );
                }
                if (field.field_type === 'select' && field.options?.choices) {
                  return (
                    <ConfigField key={field.id} label={field.name + (field.is_required ? ' *' : '')}>
                      <SelectInput
                        value={(config.fieldValues || {})[field.slug] ?? ''}
                        onChange={(v) => updateFieldValue(field.slug, v)}
                        options={[
                          { value: '', label: '— Select —' },
                          ...field.options.choices.map((c) => ({
                            value: typeof c === 'string' ? c : c.value,
                            label: typeof c === 'string' ? c : c.label,
                          })),
                        ]}
                      />
                    </ConfigField>
                  );
                }
                return (
                  <ReferenceField
                    key={field.id}
                    label={field.name + (field.is_required ? ' *' : '')}
                    value={(config.fieldValues || {})[field.slug] || ''}
                    onChange={(v) => updateFieldValue(field.slug, v)}
                    placeholder={field.field_type === 'date' ? 'YYYY-MM-DD' : `Enter ${field.name.toLowerCase()}…`}
                    upstreamNodes={upstreamNodes}
                    multiline={field.field_type === 'rich_text'}
                    rows={field.field_type === 'rich_text' ? 3 : undefined}
                  />
                );
              })}
            </>
          ) : (
            <p className="text-[11px] text-gray-400 italic">No fields defined for this object type.</p>
          )}
        </>
      )}
    </div>
  );
}

function RecordChangeConfig({ config, onChange, onOutputSchemaUpdate }) {
  const update = (key, value) => onChange({ ...config, [key]: value });

  const [objectDefs, setObjectDefs] = useState([]);
  const [loadingDefs, setLoadingDefs] = useState(true);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [showDelay, setShowDelay] = useState(!!config.delaySeconds);

  // Load object definitions on mount
  useEffect(() => {
    setLoadingDefs(true);
    apiRequest('/crm/objects/')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { setObjectDefs(data); setLoadingDefs(false); })
      .catch(() => setLoadingDefs(false));
  }, []);

  // Load field definitions when object type changes
  const loadFields = useCallback((objDefId) => {
    if (!objDefId) { setFieldDefs([]); return; }
    setLoadingFields(true);
    apiRequest(`/crm/objects/${objDefId}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        const fields = data.field_definitions || [];
        setFieldDefs(fields);
        setLoadingFields(false);

        // Update output schema dynamically
        if (onOutputSchemaUpdate) {
          const schema = [
            { path: 'record_id', label: 'Record ID', type: 'string' },
            { path: 'object_definition_id', label: 'Object Type ID', type: 'string' },
            { path: 'event_type', label: 'Event Type', type: 'string' },
            ...fields.map((f) => ({
              path: `data.${f.slug}`,
              label: f.name,
              type: f.field_type === 'number' ? 'number' : f.field_type === 'boolean' ? 'boolean' : 'string',
            })),
            ...fields.map((f) => ({
              path: `changed_fields.${f.slug}.old`,
              label: `${f.name} (Old Value)`,
              type: f.field_type === 'number' ? 'number' : 'string',
            })),
            ...fields.map((f) => ({
              path: `changed_fields.${f.slug}.new`,
              label: `${f.name} (New Value)`,
              type: f.field_type === 'number' ? 'number' : 'string',
            })),
          ];
          onOutputSchemaUpdate(schema);
        }
      })
      .catch(() => setLoadingFields(false));
  }, [onOutputSchemaUpdate]);

  useEffect(() => {
    loadFields(config.objectDefinitionId);
  }, [config.objectDefinitionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleWatchField = (slug) => {
    const current = config.watchFields || [];
    const updated = current.includes(slug)
      ? current.filter((s) => s !== slug)
      : [...current, slug];
    update('watchFields', updated);
  };

  return (
    <div className="space-y-3">
      {/* Object Type Selector */}
      <ConfigField label="Object Type">
        {loadingDefs ? (
          <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : (
          <SelectInput
            value={config.objectDefinitionId || ''}
            onChange={(v) => {
              onChange({ ...config, objectDefinitionId: v, watchFields: [], conditions: [] });
            }}
            options={[
              { value: '', label: 'Select an object type…' },
              ...objectDefs.map((od) => ({ value: od.id, label: od.name })),
            ]}
          />
        )}
      </ConfigField>

      {/* Event Type */}
      <ConfigField label="Event Type">
        <SelectInput
          value={config.eventType || 'any'}
          onChange={(v) => update('eventType', v)}
          options={[
            { value: 'any', label: 'Any Change' },
            { value: 'created', label: 'Record Created' },
            { value: 'updated', label: 'Record Updated' },
            { value: 'deleted', label: 'Record Deleted' },
          ]}
        />
      </ConfigField>

      {/* Watch Fields - only for "updated" or "any" event type */}
      {(config.eventType === 'updated' || config.eventType === 'any') && config.objectDefinitionId && (
        <ConfigField label="Watch Fields" hint="Only trigger when these fields change. Leave empty to watch all fields.">
          {loadingFields ? (
            <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : fieldDefs.length > 0 ? (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {fieldDefs.map((field) => (
                <label key={field.id} className="flex items-center gap-2 py-0.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={(config.watchFields || []).includes(field.slug)}
                    onChange={() => toggleWatchField(field.slug)}
                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-300 w-3.5 h-3.5"
                  />
                  <span className="text-[13px] text-gray-600 group-hover:text-gray-900">{field.name}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 italic">No fields defined for this object type.</p>
          )}
        </ConfigField>
      )}

      {/* Conditions */}
      {config.objectDefinitionId && (
        <ConfigField label="Conditions" hint="All conditions must be met for the workflow to trigger.">
          <div className="space-y-2">
            {(config.conditions || []).map((cond, idx) => {
              const conditions = config.conditions || [];
              const updateCondition = (key, value) => {
                const updated = [...conditions];
                updated[idx] = { ...updated[idx], [key]: value };
                update('conditions', updated);
              };
              const removeCondition = () => {
                update('conditions', conditions.filter((_, i) => i !== idx));
              };
              const isEmptyOp = cond.operator === 'is_empty' || cond.operator === 'is_not_empty';
              const isCreated = config.eventType === 'created';
              const selectedFieldDef = fieldDefs.find((f) => f.slug === cond.field);
              const fieldChoices = (selectedFieldDef?.field_type === 'select' || selectedFieldDef?.field_type === 'multi_select')
                ? (selectedFieldDef?.options?.choices || [])
                : [];

              return (
                <div key={idx} className="flex flex-col gap-1.5 p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <select
                      value={cond.field || ''}
                      onChange={(e) => updateCondition('field', e.target.value)}
                      className="flex-1 px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white min-w-0"
                    >
                      <option value="">Field…</option>
                      {fieldDefs.map((f) => (
                        <option key={f.slug} value={f.slug}>{f.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={removeCondition}
                      className="p-0.5 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <select
                      value={cond.source || 'new'}
                      onChange={(e) => updateCondition('source', e.target.value)}
                      className="px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                    >
                      <option value="new">New value</option>
                      {!isCreated && <option value="old">Old value</option>}
                      <option value="current">Current value</option>
                    </select>
                    <select
                      value={cond.operator || 'equals'}
                      onChange={(e) => updateCondition('operator', e.target.value)}
                      className="px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                    >
                      <option value="equals">equals</option>
                      <option value="not_equals">not equals</option>
                      <option value="contains">contains</option>
                      <option value="not_contains">not contains</option>
                      <option value="is_empty">is empty</option>
                      <option value="is_not_empty">is not empty</option>
                    </select>
                  </div>
                  {!isEmptyOp && (
                    fieldChoices.length > 0 ? (
                      <select
                        value={cond.value || ''}
                        onChange={(e) => updateCondition('value', e.target.value)}
                        className="w-full px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                      >
                        <option value="">Select value…</option>
                        {fieldChoices.map((choice) => {
                          const val = typeof choice === 'string' ? choice : choice.value;
                          const label = typeof choice === 'string' ? choice : choice.label;
                          return <option key={val} value={val}>{label}</option>;
                        })}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={cond.value || ''}
                        onChange={(e) => updateCondition('value', e.target.value)}
                        placeholder="Value…"
                        className="w-full px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 placeholder:text-gray-300"
                      />
                    )
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => update('conditions', [...(config.conditions || []), { field: '', source: 'new', operator: 'equals', value: '' }])}
              className="flex items-center gap-1 text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add condition
            </button>
          </div>
        </ConfigField>
      )}

      {/* Delay Trigger */}
      <ConfigField label="Delay Trigger">
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <button
              type="button"
              onClick={() => {
                const newShow = !showDelay;
                setShowDelay(newShow);
                if (!newShow) update('delaySeconds', null);
              }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showDelay ? 'bg-gray-900' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${showDelay ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
            </button>
            <span className="text-[13px] text-gray-600">
              {showDelay ? 'Delay before triggering' : 'Trigger immediately'}
            </span>
          </label>

          {showDelay && (
            <>
              <div className="flex gap-1.5">
                {[60, 120, 300].map((sec) => (
                  <button
                    key={sec}
                    type="button"
                    onClick={() => update('delaySeconds', sec)}
                    className={`px-2 py-1 text-[11px] rounded-md border transition-colors ${
                      config.delaySeconds === sec
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {sec === 60 ? '1 min' : sec === 120 ? '2 min' : '5 min'}
                  </button>
                ))}
                <input
                  type="number"
                  value={![60, 120, 300].includes(config.delaySeconds) ? (config.delaySeconds || '') : ''}
                  onChange={(e) => update('delaySeconds', e.target.value ? Number(e.target.value) : null)}
                  placeholder="Custom (sec)"
                  min={1}
                  className="flex-1 px-2 py-1 text-[11px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 placeholder:text-gray-300 min-w-0"
                />
              </div>
              <p className="text-[10px] text-gray-400 leading-snug">
                If another change happens during the delay, the timer resets. Only the latest change triggers the workflow.
              </p>
            </>
          )}
        </div>
      </ConfigField>
    </div>
  );
}

function NodeConfigFields({ nodeType, config, onChange, upstreamNodes, workflowId, onOutputSchemaUpdate, onSaveAndNavigate }) {
  const update = (key, value) => onChange({ ...config, [key]: value });

  // If this node is inside a loop, show a helpful banner listing the iterate node's reference paths
  const iterateUpstream = upstreamNodes?.find((n) => n.nodeType === 'iterate');

  const loopContextBanner = iterateUpstream && nodeType !== 'iterate' ? (
    <div className="mb-4 p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
      <p className="text-[11px] font-semibold text-amber-700 mb-1">Inside loop: {iterateUpstream.label}</p>
      <p className="text-[11px] text-amber-600 mb-1.5">
        Use these references to access each item as the loop runs:
      </p>
      <div className="space-y-0.5">
        {[
          { ref: `{{${iterateUpstream.id}.item}}`, desc: 'Whole item object' },
          { ref: `{{${iterateUpstream.id}.item.name}}`, desc: 'Name field' },
          { ref: `{{${iterateUpstream.id}.item.id}}`, desc: 'ID field' },
          { ref: `{{${iterateUpstream.id}.index}}`, desc: 'Position (0-based)' },
        ].map(({ ref, desc }) => (
          <div key={ref} className="flex items-center gap-1.5">
            <code className="text-[10px] font-mono bg-amber-100 text-amber-800 px-1 py-0.5 rounded flex-shrink-0">{ref}</code>
            <span className="text-[10px] text-amber-500">{desc}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-amber-500 mt-1.5">
        For other fields use the {'{'} {'}'} picker or type e.g. <code className="font-mono bg-amber-100 px-0.5 rounded">{'{{' + iterateUpstream.id + '.item.address}}'}</code>
      </p>
    </div>
  ) : null;

  const fields = (() => { switch (nodeType) {
    case 'cron':
      return (
        <>
          <ConfigField label="Schedule" hint='Cron format, e.g. "0 9 * * *" for daily at 9 AM'>
            <TextInput value={config.schedule} onChange={(v) => update('schedule', v)} placeholder="0 9 * * *" />
          </ConfigField>
          <ConfigField label="Timezone">
            <SelectInput
              value={config.timezone}
              onChange={(v) => update('timezone', v)}
              options={[
                { value: 'UTC', label: 'UTC' },
                { value: 'America/New_York', label: 'Eastern (US)' },
                { value: 'America/Chicago', label: 'Central (US)' },
                { value: 'America/Denver', label: 'Mountain (US)' },
                { value: 'America/Los_Angeles', label: 'Pacific (US)' },
                { value: 'Europe/London', label: 'London' },
                { value: 'Europe/Copenhagen', label: 'Copenhagen' },
                { value: 'Europe/Berlin', label: 'Berlin' },
                { value: 'Asia/Tokyo', label: 'Tokyo' },
              ]}
            />
          </ConfigField>
        </>
      );

    case 'manual':
      return (
        <p className="text-[13px] text-gray-400 italic">
          This workflow will start when you click "Run". No setup needed.
        </p>
      );

    case 'workflowTrigger':
      return (
        <ResourceSelector
          label="Source Workflow"
          value={config.sourceWorkflowId}
          onChange={(v) => update('sourceWorkflowId', v)}
          endpoint="/workflows/"
          mapItem={(w) => ({ value: w.id, label: w.name })}
          filterItems={(items) => items.filter((i) => i.value !== workflowId)}
          placeholder="Select a workflow…"
          emptyIcon={GitBranch}
          emptyTitle="No other workflows"
          emptyMessage="Create another workflow first, then come back to link it as a trigger."
          emptyAction="Save Work & Create Workflow"
          onEmptyAction={onSaveAndNavigate ? () => { onSaveAndNavigate('/workflows'); } : undefined}
        />
      );

    case 'callLLM':
      return (
        <>
          <ConfigField label="Model">
            <SelectInput
              value={config.model}
              onChange={(v) => update('model', v)}
              options={[
                { value: 'claude-sonnet', label: 'Claude Sonnet' },
                { value: 'claude-haiku', label: 'Claude Haiku' },
                { value: 'claude-opus', label: 'Claude Opus' },
              ]}
            />
          </ConfigField>
          <ReferenceField
            label="System Prompt"
            value={config.systemPrompt}
            onChange={(v) => update('systemPrompt', v)}
            placeholder="You are a helpful assistant that..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
            expandable
            promptType="system"
          />
          <ReferenceField
            label="User Prompt"
            hint="Reference previous node outputs with the { } picker"
            value={config.userPrompt}
            onChange={(v) => update('userPrompt', v)}
            placeholder="Summarize the following data..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
            expandable
            promptType="user"
          />
        </>
      );

    case 'fetchWebsite':
      return (
        <>
          <ReferenceField
            label="URL"
            value={config.url}
            onChange={(v) => update('url', v)}
            placeholder="https://example.com/api/data"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Method">
            <SelectInput
              value={config.method}
              onChange={(v) => update('method', v)}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'DELETE', label: 'DELETE' },
              ]}
            />
          </ConfigField>
          <ReferenceField
            label="Headers (JSON)"
            hint='e.g. {"Authorization": "Bearer ..."}'
            value={config.headers}
            onChange={(v) => update('headers', v)}
            placeholder='{"Content-Type": "application/json"}'
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
          />
        </>
      );

    case 'getWebsiteContent':
      return (
        <>
          <ReferenceField
            label="Website URL"
            hint="Paste any public web page link. The page must be open to everyone — pages behind a login won't work."
            value={config.url}
            onChange={(v) => update('url', v)}
            placeholder="https://example.com/blog/post"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="When to read the page">
            <RichSelect
              value={config.waitUntil || 'load'}
              onChange={(v) => update('waitUntil', v)}
              options={[
                {
                  value: 'load',
                  label: 'When the page has loaded',
                  description: 'Read the page once it finishes loading. Works well for almost every website.',
                  icon: Zap,
                  recommended: true,
                },
                {
                  value: 'domcontentloaded',
                  label: 'As soon as the text is ready',
                  description: 'Faster, but skips images and ads. Use if you only need the text.',
                  icon: FileCode,
                },
                {
                  value: 'networkidle2',
                  label: 'After the page calms down',
                  description: 'Waits a moment for extra content to appear. Good for slow or busy sites.',
                  icon: Wifi,
                },
                {
                  value: 'networkidle0',
                  label: 'After everything finishes',
                  description: 'Waits the longest. Use only if the page keeps loading content for a while.',
                  icon: WifiOff,
                },
              ]}
            />
          </ConfigField>

          <AdvancedSettings>
            <ReferenceField
              label="Wait for a specific section"
              hint="Tell us a part of the page to wait for before reading (a CSS selector, e.g. .article-body). Leave empty if you're not sure."
              value={config.waitForSelector}
              onChange={(v) => update('waitForSelector', v)}
              placeholder=".article-body"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Pretend to be a specific browser"
              hint="Optional. Some sites only show content to certain browsers — paste a user agent string here if needed."
              value={config.userAgent}
              onChange={(v) => update('userAgent', v)}
              placeholder="Mozilla/5.0 ..."
              upstreamNodes={upstreamNodes}
            />
          </AdvancedSettings>
        </>
      );

    case 'linkedinPersonFinder':
      return (
        <>
          <ReferenceField
            label="First Name"
            value={config.firstName}
            onChange={(v) => update('firstName', v)}
            placeholder="Sarah"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Last Name"
            value={config.lastName}
            onChange={(v) => update('lastName', v)}
            placeholder="Chen"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Profile Detail">
            <SelectInput
              value={config.profileScraperMode || 'Short'}
              onChange={(v) => update('profileScraperMode', v)}
              options={[
                { value: 'Short', label: 'Short' },
                { value: 'Full', label: 'Full' },
                { value: 'Full + email search', label: 'Full + email search' },
              ]}
            />
          </ConfigField>
          <ConfigField label="Strict Search">
            <SelectInput
              value={config.strictSearch === false ? 'false' : 'true'}
              onChange={(v) => update('strictSearch', v === 'true')}
              options={[
                { value: 'true', label: 'Match first and last name exactly' },
                { value: 'false', label: 'Allow broader matches' },
              ]}
            />
          </ConfigField>
          <div className="grid grid-cols-2 gap-2">
            <ConfigField label="Pages">
              <NumberInput
                value={config.maxPages}
                onChange={(v) => update('maxPages', v)}
                min={1}
                max={10}
                placeholder="1"
              />
            </ConfigField>
            <ConfigField label="Profiles">
              <NumberInput
                value={config.maxItems}
                onChange={(v) => update('maxItems', v)}
                min={1}
                max={50}
                placeholder="5"
              />
            </ConfigField>
          </div>
          <AdvancedSettings label="Filters">
            <ReferenceField
              label="Locations"
              hint="Comma-separated, e.g. Copenhagen, London"
              value={config.locations}
              onChange={(v) => update('locations', v)}
              placeholder="Copenhagen"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Current Companies"
              hint="Comma-separated LinkedIn company URLs"
              value={config.currentCompanies}
              onChange={(v) => update('currentCompanies', v)}
              placeholder="https://www.linkedin.com/company/acme"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Past Companies"
              hint="Comma-separated LinkedIn company URLs"
              value={config.pastCompanies}
              onChange={(v) => update('pastCompanies', v)}
              placeholder="https://www.linkedin.com/company/globex"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Schools"
              hint="Comma-separated school names"
              value={config.schools}
              onChange={(v) => update('schools', v)}
              placeholder="Stanford University"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Current Job Titles"
              hint="Comma-separated exact titles"
              value={config.currentJobTitles}
              onChange={(v) => update('currentJobTitles', v)}
              placeholder="Head of Sales, VP Marketing"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Industry IDs"
              hint="Comma-separated LinkedIn industry IDs"
              value={config.industryIds}
              onChange={(v) => update('industryIds', v)}
              placeholder="4"
              upstreamNodes={upstreamNodes}
            />
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'linkedinCompanyEmployees':
      return (
        <>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-700">
            Finds employee profiles for one or more LinkedIn companies and returns normalized profile fields you can loop through with an <span className="font-medium">iterate</span> node.
          </div>
          <ReferenceField
            label="LinkedIn Company URLs"
            hint="Required. Add one or more LinkedIn company URLs, separated by commas or new lines."
            value={config.companyUrls}
            onChange={(v) => update('companyUrls', v)}
            placeholder="https://www.linkedin.com/company/google"
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
          <ConfigField label="Profile Detail">
            <SelectInput
              value={config.profileScraperMode || 'Short ($4 per 1k)'}
              onChange={(v) => update('profileScraperMode', v)}
              options={[
                { value: 'Short ($4 per 1k)', label: 'Short ($4 per 1k)' },
                { value: 'Full ($8 per 1k)', label: 'Full ($8 per 1k)' },
                { value: 'Full + email search ($12 per 1k)', label: 'Full + email search ($12 per 1k)' },
              ]}
            />
          </ConfigField>
          <ConfigField label="Profiles" hint="Default 25, capped at 100 to control paid actor spend.">
            <NumberInput
              value={config.maxItems}
              onChange={(v) => update('maxItems', v)}
              min={1}
              max={100}
              placeholder="25"
            />
          </ConfigField>
          <AdvancedSettings label="Filters">
            <ReferenceField
              label="Locations"
              hint="Comma-separated LinkedIn location names"
              value={config.locations}
              onChange={(v) => update('locations', v)}
              placeholder="San Francisco, Copenhagen"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Search Query"
              hint="Optional fuzzy search query"
              value={config.searchQuery}
              onChange={(v) => update('searchQuery', v)}
              placeholder="Founder"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Job Titles"
              hint="Comma-separated strict titles"
              value={config.jobTitles}
              onChange={(v) => update('jobTitles', v)}
              placeholder="Software Engineer, Product Manager"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Industry IDs"
              hint="Comma-separated LinkedIn industry IDs"
              value={config.industryIds}
              onChange={(v) => update('industryIds', v)}
              placeholder="4, 43"
              upstreamNodes={upstreamNodes}
            />
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'googleSearchResults':
      return (
        <>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-700">
            Monitors Google SERPs for one or more queries and returns normalized organic results, People Also Ask questions, and optional ads for downstream automation.
          </div>
          <ReferenceField
            label="Queries"
            hint="Required. Add one or more search queries, separated by commas or new lines."
            value={config.queries}
            onChange={(v) => update('queries', v)}
            placeholder="best crm for startups"
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
          <div className="grid grid-cols-2 gap-2">
            <ReferenceField
              label="Country Code"
              hint="ISO 2-letter code."
              value={config.countryCode}
              onChange={(v) => update('countryCode', v)}
              placeholder="US"
              upstreamNodes={upstreamNodes}
            />
            <ReferenceField
              label="Language Code"
              hint="ISO language code."
              value={config.languageCode}
              onChange={(v) => update('languageCode', v)}
              placeholder="en"
              upstreamNodes={upstreamNodes}
            />
          </div>
          <ConfigField label="Pages Per Query" hint="Default 1, capped at 3 to control paid actor spend.">
            <NumberInput
              value={config.maxPagesPerQuery}
              onChange={(v) => update('maxPagesPerQuery', v)}
              min={1}
              max={3}
              placeholder="1"
            />
          </ConfigField>
          <AdvancedSettings label="Result Options">
            <ConfigField label="Include People Also Ask">
              <SelectInput
                value={config.includePeopleAlsoAsk === false ? 'false' : 'true'}
                onChange={(v) => update('includePeopleAlsoAsk', v === 'true')}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
              />
            </ConfigField>
            <ConfigField label="Include Ads">
              <SelectInput
                value={config.includeAds ? 'true' : 'false'}
                onChange={(v) => update('includeAds', v === 'true')}
                options={[
                  { value: 'false', label: 'No' },
                  { value: 'true', label: 'Yes' },
                ]}
              />
            </ConfigField>
            <ConfigField label="Include AI Overview">
              <SelectInput
                value={config.includeAiOverview ? 'true' : 'false'}
                onChange={(v) => update('includeAiOverview', v === 'true')}
                options={[
                  { value: 'false', label: 'No' },
                  { value: 'true', label: 'Yes' },
                ]}
              />
            </ConfigField>
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'linkedinJobsSearch':
      return (
        <>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-700">
            Tracks LinkedIn job-posting signals across search terms and returns normalized job data plus a deduplicated company list for downstream targeting.
          </div>
          <ReferenceField
            label="Search Terms"
            hint="Required. Add one or more terms, separated by commas or new lines."
            value={config.searchTerms}
            onChange={(v) => update('searchTerms', v)}
            placeholder="product manager, demand generation"
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
          <ConfigField label="Max Jobs" hint="Default 25, capped at 100 to control paid actor spend.">
            <NumberInput
              value={config.maxItems}
              onChange={(v) => update('maxItems', v)}
              min={1}
              max={100}
              placeholder="25"
            />
          </ConfigField>
          <AdvancedSettings label="Filters">
            <ReferenceField
              label="Locations"
              hint="Optional comma/newline list, e.g. Copenhagen, New York"
              value={config.locations}
              onChange={(v) => update('locations', v)}
              placeholder="Copenhagen, New York"
              upstreamNodes={upstreamNodes}
              multiline
              rows={2}
            />
            <ReferenceField
              label="LinkedIn Company URLs"
              hint="Optional comma/newline list of LinkedIn company URLs."
              value={config.companyUrls}
              onChange={(v) => update('companyUrls', v)}
              placeholder="https://www.linkedin.com/company/acme"
              upstreamNodes={upstreamNodes}
              multiline
              rows={2}
            />
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'googleMapsReviews':
      return (
        <>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-700">
            Collects and normalizes Google Maps reviews for one or more places so you can filter by rating and loop through each review downstream.
          </div>
          <ReferenceField
            label="Google Maps Place URLs"
            hint="Required. Add one or more Google Maps place URLs, separated by commas or new lines."
            value={config.placeUrls}
            onChange={(v) => update('placeUrls', v)}
            placeholder="https://www.google.com/maps/place/..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
          <ConfigField label="Max Reviews" hint="Default 25, capped at 200 to control paid actor spend.">
            <NumberInput
              value={config.maxReviews}
              onChange={(v) => update('maxReviews', v)}
              min={1}
              max={200}
              placeholder="25"
            />
          </ConfigField>
          <ConfigField label="Sort">
            <SelectInput
              value={config.sort || 'newest'}
              onChange={(v) => update('sort', v)}
              options={[
                { value: 'newest', label: 'Newest' },
                { value: 'mostRelevant', label: 'Most relevant' },
                { value: 'highestRating', label: 'Highest rating' },
                { value: 'lowestRating', label: 'Lowest rating' },
              ]}
            />
          </ConfigField>
          <AdvancedSettings label="Filters">
            <ConfigField label="Minimum Rating" hint="Optional post-processing filter applied to normalized reviews.">
              <NumberInput
                value={config.minRating}
                onChange={(v) => update('minRating', v)}
                min={1}
                max={5}
                placeholder="4"
              />
            </ConfigField>
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'facebookAdsLibrarySearch':
      return (
        <>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-700">
            Monitors competitor or brand ads from the Meta Ad Library and returns normalized ad, platform, and landing-page fields for downstream analysis.
          </div>
          <ReferenceField
            label="Search Terms"
            hint="Optional. Add one or more keywords separated by commas or new lines."
            value={config.searchTerms}
            onChange={(v) => update('searchTerms', v)}
            placeholder="nike, competitor brand name"
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
          <ReferenceField
            label="Facebook Page IDs"
            hint="Optional. Add one or more page IDs separated by commas or new lines."
            value={config.pageIds}
            onChange={(v) => update('pageIds', v)}
            placeholder="15087023444"
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
          />
          <ReferenceField
            label="Ad Library URLs"
            hint="Optional. Paste Meta Ad Library search URLs to preserve exact filters."
            value={config.adLibraryUrls}
            onChange={(v) => update('adLibraryUrls', v)}
            placeholder="https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=US&q=nike"
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
          />
          <ReferenceField
            label="Country Codes"
            hint="Optional comma/newline list (ISO 2-letter), or ALL. Used for generated search URLs."
            value={config.countries}
            onChange={(v) => update('countries', v)}
            placeholder="US, GB, DE"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Status Filter">
            <SelectInput
              value={config.activeStatus || 'active'}
              onChange={(v) => update('activeStatus', v)}
              options={[
                { value: 'active', label: 'Active ads only' },
                { value: 'inactive', label: 'Inactive ads only' },
                { value: 'all', label: 'All ads' },
              ]}
            />
          </ConfigField>
          <ConfigField label="Max Ads" hint="Default 25, capped at 100 to control paid actor spend.">
            <NumberInput
              value={config.maxItems}
              onChange={(v) => update('maxItems', v)}
              min={1}
              max={100}
              placeholder="25"
            />
          </ConfigField>
          <AdvancedSettings label="Result Options">
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'instagramProfileSnapshot':
      return (
        <>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-700">
            Captures public Instagram profile snapshots for one or more usernames and returns normalized account metrics with optional recent posts.
          </div>
          <ReferenceField
            label="Profile Usernames or URLs"
            hint="Required. Add Instagram usernames or profile URLs, separated by commas or new lines."
            value={config.profileUrlsOrUsernames}
            onChange={(v) => update('profileUrlsOrUsernames', v)}
            placeholder="@natgeo or https://www.instagram.com/natgeo/"
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
          <ConfigField label="Max Profiles" hint="Default 25, capped at 100 to control paid actor spend.">
            <NumberInput
              value={config.maxItems}
              onChange={(v) => update('maxItems', v)}
              min={1}
              max={100}
              placeholder="25"
            />
          </ConfigField>
          <AdvancedSettings label="Result Options">
            <ConfigField label="Include About Section" hint="Optional paid actor field with profile join date and related metadata when available.">
              <SelectInput
                value={config.includeAboutSection ? 'true' : 'false'}
                onChange={(v) => update('includeAboutSection', v === 'true')}
                options={[
                  { value: 'false', label: 'No' },
                  { value: 'true', label: 'Yes' },
                ]}
              />
            </ConfigField>
            <ConfigField label="Include Recent Posts">
              <SelectInput
                value={config.includeRecentPosts === false ? 'false' : 'true'}
                onChange={(v) => update('includeRecentPosts', v === 'true')}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
              />
            </ConfigField>
            {(
              config.includeRecentPosts === true
              || config.includeRecentPosts === 'true'
              || config.includeRecentPosts === undefined
              || config.includeRecentPosts === null
            ) && (
              <ConfigField label="Max Recent Posts" hint="Default 6, capped at 25 in normalized output.">
                <NumberInput
                  value={config.maxPosts}
                  onChange={(v) => update('maxPosts', v)}
                  min={1}
                  max={25}
                  placeholder="6"
                />
              </ConfigField>
            )}
            <ConfigField label="Raw Apify Items" hint="Stores full actor records in workflow run history.">
              <SelectInput
                value={config.includeRawItems ? 'true' : 'false'}
                onChange={(v) => update('includeRawItems', v === 'true')}
                options={[
                  { value: 'false', label: 'Do not store raw items' },
                  { value: 'true', label: 'Store raw items' },
                ]}
              />
            </ConfigField>
          </AdvancedSettings>
        </>
      );

    case 'createCompany':
      return (
        <>
          <ReferenceField
            label="Company Name"
            value={config.name}
            onChange={(v) => update('name', v)}
            placeholder="Acme Corp"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Additional Fields (JSON)"
            hint='e.g. {"industry": "Tech", "website": "https://..."}'
            value={config.fields}
            onChange={(v) => update('fields', v)}
            placeholder='{"industry": "SaaS"}'
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
        </>
      );

    case 'getCompany':
      return (
        <ReferenceField
          label="Company ID"
          hint="Use output from a previous step"
          value={config.companyId}
          onChange={(v) => update('companyId', v)}
          placeholder="{{search_1.results[0].id}}"
          upstreamNodes={upstreamNodes}
        />
      );

    case 'updateCompany':
      return (
        <>
          <ReferenceField
            label="Company ID"
            hint="Use output from a previous step"
            value={config.companyId}
            onChange={(v) => update('companyId', v)}
            placeholder="{{search_1.results[0].id}}"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Fields to Update (JSON)"
            value={config.fields}
            onChange={(v) => update('fields', v)}
            placeholder='{"status": "qualified", "industry": "Tech"}'
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
        </>
      );

    case 'createPerson':
      return (
        <>
          <ReferenceField
            label="First Name"
            value={config.firstName}
            onChange={(v) => update('firstName', v)}
            placeholder="Sarah"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Last Name"
            value={config.lastName}
            onChange={(v) => update('lastName', v)}
            placeholder="Chen"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Additional Fields (JSON)"
            hint='e.g. {"email": "sarah@acme.com", "title": "CTO"}'
            value={config.fields}
            onChange={(v) => update('fields', v)}
            placeholder='{"email": "sarah@acme.com"}'
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
        </>
      );

    case 'getPerson':
      return (
        <ReferenceField
          label="Person ID"
          hint="Use output from a previous step"
          value={config.personId}
          onChange={(v) => update('personId', v)}
          placeholder="{{search_1.results[0].id}}"
          upstreamNodes={upstreamNodes}
        />
      );

    case 'updatePerson':
      return (
        <>
          <ReferenceField
            label="Person ID"
            hint="Use output from a previous step"
            value={config.personId}
            onChange={(v) => update('personId', v)}
            placeholder="{{search_1.results[0].id}}"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Fields to Update (JSON)"
            value={config.fields}
            onChange={(v) => update('fields', v)}
            placeholder='{"title": "VP of Engineering"}'
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
        </>
      );

    case 'createDeal':
      return (
        <>
          <ReferenceField
            label="Deal Name"
            value={config.name}
            onChange={(v) => update('name', v)}
            placeholder="Enterprise License Deal"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Company ID"
            value={config.companyId}
            onChange={(v) => update('companyId', v)}
            placeholder="{{get_company_1.record.id}}"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Pipeline ID"
            value={config.pipelineId}
            onChange={(v) => update('pipelineId', v)}
            placeholder="pipeline_id"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Stage ID"
            value={config.stageId}
            onChange={(v) => update('stageId', v)}
            placeholder="stage_id"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Owner ID"
            value={config.ownerId}
            onChange={(v) => update('ownerId', v)}
            placeholder="user_id"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Value"
            value={config.value}
            onChange={(v) => update('value', v)}
            placeholder="50000"
            upstreamNodes={upstreamNodes}
          />
        </>
      );

    case 'getDeal':
      return (
        <ReferenceField
          label="Deal ID"
          hint="Use output from a previous step"
          value={config.dealId}
          onChange={(v) => update('dealId', v)}
          placeholder="{{search_1.results[0].id}}"
          upstreamNodes={upstreamNodes}
        />
      );

    case 'updateDeal':
      return (
        <>
          <ReferenceField
            label="Deal ID"
            hint="Use output from a previous step"
            value={config.dealId}
            onChange={(v) => update('dealId', v)}
            placeholder="{{search_1.results[0].id}}"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Fields to Update (JSON)"
            value={config.fields}
            onChange={(v) => update('fields', v)}
            placeholder='{"value": 75000, "stage_id": "..."}'
            upstreamNodes={upstreamNodes}
            multiline
            rows={3}
          />
        </>
      );

    case 'createRecord':
    case 'getRecord':
    case 'updateRecord':
    case 'deleteRecord':
    case 'searchRecords':
      return (
        <CrmRecordConfig
          nodeType={nodeType}
          config={config}
          onChange={onChange}
          upstreamNodes={upstreamNodes}
          onOutputSchemaUpdate={onOutputSchemaUpdate}
        />
      );

    case 'searchCRM':
      return (
        <>
          <ConfigField label="Record Type">
            <SelectInput
              value={config.recordType}
              onChange={(v) => update('recordType', v)}
              options={[
                { value: 'company', label: 'Company' },
                { value: 'person', label: 'Person' },
              ]}
            />
          </ConfigField>
          <ReferenceField
            label="Search Query"
            hint="Search by name, email, or other fields"
            value={config.query}
            onChange={(v) => update('query', v)}
            placeholder='industry:"SaaS" AND country:"Denmark"'
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Limit">
            <NumberInput value={config.limit} onChange={(v) => update('limit', v)} min={1} max={100} placeholder="10" />
          </ConfigField>
        </>
      );

    case 'iterate':
      return (
        <>
          <ReferenceField
            label="Source Field"
            hint="Reference output from a previous node that returns a list"
            value={config.sourceField}
            onChange={(v) => update('sourceField', v)}
            placeholder="{{search_1.results}}"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField
            label="Max Iterations"
            hint="Leave empty to process all items. Set a limit to cap iterations (e.g. 100)."
          >
            <NumberInput
              value={config.max_iterations}
              onChange={(v) => update('max_iterations', v)}
              min={1}
              placeholder="All items"
            />
          </ConfigField>
          <ConfigField label="Continue on Error">
            <SelectInput
              value={config.continue_on_error ? 'true' : 'false'}
              onChange={(v) => update('continue_on_error', v === 'true')}
              options={[
                { value: 'false', label: 'Stop on first error' },
                { value: 'true', label: 'Continue to next item on error' },
              ]}
            />
          </ConfigField>
        </>
      );

    case 'sendEmail':
      return (
        <>
          <ReferenceField
            label="To"
            value={config.to}
            onChange={(v) => update('to', v)}
            placeholder="user@example.com"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Subject"
            value={config.subject}
            onChange={(v) => update('subject', v)}
            placeholder="Weekly CRM Summary Report"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Body"
            value={config.body}
            onChange={(v) => update('body', v)}
            placeholder="Hi there,\n\nHere is your weekly summary..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={4}
          />
        </>
      );

    case 'conditional':
      return (
        <>
          <ReferenceField
            label="Condition"
            hint="Expression that evaluates to true or false"
            value={config.condition}
            onChange={(v) => update('condition', v)}
            placeholder='{{llm_1.output.sentiment}} === "positive"'
            upstreamNodes={upstreamNodes}
          />
          <div className="flex gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-xs text-gray-500">True path</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <span className="text-xs text-gray-500">False path</span>
            </div>
          </div>
        </>
      );

    case 'stop':
      return (
        <ReferenceField
          label="Reason (optional)"
          hint="A message explaining why the workflow stopped"
          value={config.reason}
          onChange={(v) => update('reason', v)}
          placeholder="Condition not met, stopping workflow"
          upstreamNodes={upstreamNodes}
        />
      );

    case 'extractData':
      return (
        <>
          <ReferenceField
            label="Source"
            hint="Reference output from a previous node"
            value={config.source}
            onChange={(v) => update('source', v)}
            placeholder="{{previous_step.body}}"
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
          />
          <ConfigField label="Extraction Mode">
            <SelectInput
              value={config.mode}
              onChange={(v) => update('mode', v)}
              options={[
                { value: 'keyPath', label: 'Key Path (dot notation)' },
                { value: 'regex', label: 'Regular Expression' },
                { value: 'jsonPath', label: 'JSONPath' },
              ]}
            />
          </ConfigField>
          <ConfigField label="Pattern / Path" hint={
            config.mode === 'keyPath' ? 'e.g. user.name or items[0].id' :
            config.mode === 'regex' ? 'e.g. email: (\\S+@\\S+)' :
            'e.g. $.store.book[0].title'
          }>
            <TextInput
              value={config.pattern}
              onChange={(v) => update('pattern', v)}
              placeholder={
                config.mode === 'keyPath' ? 'user.name' :
                config.mode === 'regex' ? '(\\d+\\.\\d+)' :
                '$.data.results[0]'
              }
            />
          </ConfigField>
        </>
      );

    case 'parseJson':
      return (
        <ReferenceField
          label="Source (JSON string)"
          hint="Reference a JSON string from a previous node"
          value={config.source}
          onChange={(v) => update('source', v)}
          placeholder='{{http_request_1.body}}'
          upstreamNodes={upstreamNodes}
          multiline
          rows={3}
        />
      );

    case 'delay':
      return (
        <>
          <ConfigField label="Duration">
            <NumberInput
              value={config.duration}
              onChange={(v) => update('duration', v)}
              min={1}
              placeholder="5"
            />
          </ConfigField>
          <ConfigField label="Unit">
            <SelectInput
              value={config.unit}
              onChange={(v) => update('unit', v)}
              options={[
                { value: 'minutes', label: 'Minutes' },
                { value: 'hours', label: 'Hours' },
                { value: 'days', label: 'Days' },
              ]}
            />
          </ConfigField>
          <p className="text-[11px] text-gray-400 mt-2">
            Uses Cloudflare durable sleep — survives worker restarts.
          </p>
        </>
      );

    case 'router': {
      const rules = config.rules || [];
      const updateRule = (idx, key, value) => {
        const updated = [...rules];
        updated[idx] = { ...updated[idx], [key]: value };
        update('rules', updated);
      };
      const addRule = () => {
        update('rules', [...rules, { label: `Route ${rules.length + 1}`, field: '', operator: 'equals', value: '' }]);
      };
      const removeRule = (idx) => {
        update('rules', rules.filter((_, i) => i !== idx));
      };

      return (
        <>
          <ConfigField label="Routing Rules" hint="First matching rule determines the branch. Unmatched traffic goes to Default.">
            <div className="space-y-2">
              {rules.map((rule, idx) => (
                <div key={idx} className="flex flex-col gap-1.5 p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={rule.label || ''}
                      onChange={(e) => updateRule(idx, 'label', e.target.value)}
                      placeholder="Route name"
                      className="flex-1 px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white font-medium min-w-0"
                    />
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      className="p-0.5 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ReferenceField
                    label=""
                    value={rule.field || ''}
                    onChange={(v) => updateRule(idx, 'field', v)}
                    placeholder="{{previous_step.output}}"
                    upstreamNodes={upstreamNodes}
                  />
                  <div className="flex items-center gap-1.5">
                    <select
                      value={rule.operator || 'equals'}
                      onChange={(e) => updateRule(idx, 'operator', e.target.value)}
                      className="px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                    >
                      <option value="equals">equals</option>
                      <option value="not_equals">not equals</option>
                      <option value="contains">contains</option>
                      <option value="not_contains">not contains</option>
                      <option value="greater_than">greater than</option>
                      <option value="less_than">less than</option>
                      <option value="is_empty">is empty</option>
                      <option value="is_not_empty">is not empty</option>
                    </select>
                    {rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty' && (
                      <input
                        type="text"
                        value={rule.value || ''}
                        onChange={(e) => updateRule(idx, 'value', e.target.value)}
                        placeholder="Value…"
                        className="flex-1 px-2 py-1 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 placeholder:text-gray-300 min-w-0"
                      />
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addRule}
                className="flex items-center gap-1 text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add route
              </button>
            </div>
          </ConfigField>
          <div className="flex items-center gap-1.5 mt-2">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
            <span className="text-xs text-gray-500">Default (no match)</span>
          </div>
        </>
      );
    }

    case 'perplexityResearch':
      return (
        <>
          <ReferenceField
            label="Query"
            hint="What to research — can reference previous node outputs"
            value={config.query}
            onChange={(v) => update('query', v)}
            placeholder="What are the latest trends in AI?"
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
          />
          <ConfigField label="Model">
            <SelectInput
              value={config.model}
              onChange={(v) => update('model', v)}
              options={[
                { value: 'sonar', label: 'Sonar' },
                { value: 'sonar-pro', label: 'Sonar Pro' },
              ]}
            />
          </ConfigField>
          <ReferenceField
            label="System Prompt (optional)"
            value={config.systemPrompt}
            onChange={(v) => update('systemPrompt', v)}
            placeholder="You are a research analyst..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={2}
          />
        </>
      );

    case 'webSearch':
      return (
        <>
          <ReferenceField
            label="Query"
            hint="Search query — can reference previous node outputs"
            value={config.query}
            onChange={(v) => update('query', v)}
            placeholder="best CRM tools 2026"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Max Results">
            <NumberInput
              value={config.maxResults}
              onChange={(v) => update('maxResults', v)}
              min={1}
              max={20}
              placeholder="5"
            />
          </ConfigField>
        </>
      );

    case 'googlePlacesSearch':
      return (
        <>
          <ReferenceField
            label="Query"
            hint='e.g. "dentists", "restaurants", "gyms"'
            value={config.query}
            onChange={(v) => update('query', v)}
            placeholder="dentists"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Location"
            hint="City name or lat,lng coordinates"
            value={config.location}
            onChange={(v) => update('location', v)}
            placeholder="Copenhagen, Denmark"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Radius (km)">
            <NumberInput
              value={config.radius}
              onChange={(v) => update('radius', v)}
              min={1}
              max={50}
              placeholder="10"
            />
          </ConfigField>
          <ConfigField label="Max Results">
            <NumberInput
              value={config.maxResults}
              onChange={(v) => update('maxResults', v)}
              min={1}
              max={60}
              placeholder="20"
            />
          </ConfigField>
          <ConfigField label="Type Filter (optional)" hint="Google place type, e.g. restaurant, dentist, gym">
            <TextInput
              value={config.typeFilter}
              onChange={(v) => update('typeFilter', v)}
              placeholder="restaurant"
            />
          </ConfigField>
        </>
      );

    case 'gmailSendEmail':
      return (
        <>
          <ConnectionPicker provider="google" value={config.connectionId} onChange={(v) => update('connectionId', v)} />
          <ReferenceField
            label="To"
            value={config.to}
            onChange={(v) => update('to', v)}
            placeholder="user@example.com"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Subject"
            value={config.subject}
            onChange={(v) => update('subject', v)}
            placeholder="Weekly Report"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Body"
            value={config.body}
            onChange={(v) => update('body', v)}
            placeholder="Hi there,\n\nHere is your report..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={4}
          />
          <ReferenceField
            label="CC (optional)"
            value={config.cc}
            onChange={(v) => update('cc', v)}
            placeholder="cc@example.com"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="BCC (optional)"
            value={config.bcc}
            onChange={(v) => update('bcc', v)}
            placeholder="bcc@example.com"
            upstreamNodes={upstreamNodes}
          />
        </>
      );

    case 'gmailReadEmail':
      return (
        <>
          <ConnectionPicker provider="google" value={config.connectionId} onChange={(v) => update('connectionId', v)} />
          <ReferenceField
            label="Search Query"
            hint="Gmail search syntax, e.g. from:user@example.com is:unread"
            value={config.query}
            onChange={(v) => update('query', v)}
            placeholder="is:unread from:team@company.com"
            upstreamNodes={upstreamNodes}
          />
          <ConfigField label="Max Results">
            <NumberInput
              value={config.maxResults}
              onChange={(v) => update('maxResults', v)}
              min={1}
              max={50}
              placeholder="5"
            />
          </ConfigField>
        </>
      );

    case 'gmailReplyEmail':
      return (
        <>
          <ConnectionPicker provider="google" value={config.connectionId} onChange={(v) => update('connectionId', v)} />
          <ReferenceField
            label="Message ID"
            hint="The ID of the email to reply to"
            value={config.messageId}
            onChange={(v) => update('messageId', v)}
            placeholder="{{gmail_read_1.messages[0].id}}"
            upstreamNodes={upstreamNodes}
          />
          <ReferenceField
            label="Reply Body"
            value={config.body}
            onChange={(v) => update('body', v)}
            placeholder="Thank you for reaching out..."
            upstreamNodes={upstreamNodes}
            multiline
            rows={4}
          />
        </>
      );

    case 'webhook':
      return <WebhookConfig workflowId={workflowId} />;

    case 'formSubmission':
      return (
        <FormSubmissionConfig
          config={config}
          onChange={onChange}
          workflowId={workflowId}
          onOutputSchemaUpdate={onOutputSchemaUpdate}
          onSaveAndNavigate={onSaveAndNavigate}
        />
      );

    case 'recordChange':
      return (
        <RecordChangeConfig
          config={config}
          onChange={onChange}
          onOutputSchemaUpdate={onOutputSchemaUpdate}
        />
      );

    default:
      return <p className="text-sm text-gray-400">No configuration available.</p>;
  } })();

  return (
    <>
      {loopContextBanner}
      {fields}
    </>
  );
}

const NO_OUTPUT_TYPES = new Set(['conditional', 'cron', 'manual', 'webhook', 'formSubmission', 'recordChange', 'stop']);

export default function NodeConfigPanel({ node, config, onChange, onDelete, onClose, upstreamNodes, onTestRun, testOutput, workflowId, onOutputSchemaUpdate, onSaveAndNavigate }) {
  const nodeType = NODE_TYPES[node.data.nodeType];
  const [running, setRunning] = useState(false);
  const [outputExpanded, setOutputExpanded] = useState(true);

  if (!nodeType) return null;

  const canTestRun = !NO_OUTPUT_TYPES.has(node.data.nodeType);

  const handleTestRun = () => {
    setRunning(true);
    setTimeout(() => {
      const data = generateMockOutput(node.data.nodeType, config || nodeType.defaultConfig);
      const schema = data ? inferSchema(data) : [];
      onTestRun(node.id, data, schema);
      setRunning(false);
      setOutputExpanded(true);
    }, 400);
  };

  return (
    <div className="w-72 border-l border-gray-100 bg-white flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2.5">
        <div
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: nodeType.color }}
        />
        <span className="text-[13px] font-medium text-gray-900 flex-1 truncate">{nodeType.label}</span>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Config fields */}
      <div className="flex-1 overflow-y-auto p-4">
        <NodeConfigFields
          nodeType={node.data.nodeType}
          config={config || nodeType.defaultConfig}
          onChange={onChange}
          upstreamNodes={upstreamNodes}
          workflowId={workflowId}
          onOutputSchemaUpdate={onOutputSchemaUpdate}
          onSaveAndNavigate={onSaveAndNavigate}
        />

        {/* Test Run */}
        {canTestRun && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={handleTestRun}
              disabled={running}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg transition-colors bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.97]"
            >
              {running ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5" />
                  Test Run
                </>
              )}
            </button>

            {testOutput?.data && (
              <div className="mt-3">
                <button
                  onClick={() => setOutputExpanded((v) => !v)}
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors mb-1.5"
                >
                  {outputExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  Output Preview
                </button>
                {outputExpanded && (
                  <pre className="text-[11px] leading-relaxed text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2.5 overflow-x-auto max-h-48 overflow-y-auto">
                    {JSON.stringify(testOutput.data, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete button */}
      <div className="p-4 border-t border-gray-100">
        <button
          onClick={onDelete}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-gray-400 hover:text-red-500 hover:bg-red-50/50 rounded-lg transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete Node
        </button>
      </div>
    </div>
  );
}
